import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TerminalSquare } from 'lucide-react';
import { toast } from 'sonner';
import MatrixRain from '@/components/three/MatrixRain';
import { useLang } from '@/context/LanguageContext';

const SEQUENCE = ['arrowup', 'arrowup', 'arrowdown', 'arrowdown', 'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a'];

export default function MatrixMode() {
  const { t } = useLang();
  const [active, setActive] = useState(false);

  const enable = useCallback(() => {
    setActive(true);
    document.documentElement.classList.add('matrix-mode');
    toast.success(t('matrix.activated'), { description: t('matrix.hint') });
  }, [t]);

  const disable = useCallback(() => {
    setActive(false);
    document.documentElement.classList.remove('matrix-mode');
  }, []);

  // Konami detection + external toggle event
  useEffect(() => {
    let buffer = [];
    const onKey = (e) => {
      if (e.key === 'Escape' && active) { disable(); return; }
      const k = (e.key || '').toLowerCase();
      buffer = [...buffer, k].slice(-SEQUENCE.length);
      if (buffer.length === SEQUENCE.length && buffer.every((v, i) => v === SEQUENCE[i])) {
        buffer = [];
        setActive((cur) => {
          if (cur) { document.documentElement.classList.remove('matrix-mode'); return false; }
          document.documentElement.classList.add('matrix-mode');
          toast.success(t('matrix.activated'), { description: t('matrix.hint') });
          return true;
        });
      }
    };
    const onToggle = () => setActive((cur) => {
      if (cur) { document.documentElement.classList.remove('matrix-mode'); return false; }
      document.documentElement.classList.add('matrix-mode');
      toast.success(t('matrix.activated'), { description: t('matrix.hint') });
      return true;
    });
    window.addEventListener('keydown', onKey);
    window.addEventListener('itw:toggle-matrix', onToggle);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('itw:toggle-matrix', onToggle); };
  }, [active, disable, t]);

  useEffect(() => () => document.documentElement.classList.remove('matrix-mode'), []);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="wow-matrix-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[95] overflow-hidden"
          style={{ background: 'rgba(1, 6, 2, 0.86)' }}
        >
          <MatrixRain opacity={0.55} />
          {/* scanline overlay */}
          <div className="pointer-events-none absolute inset-0 opacity-20"
            style={{ background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.5) 3px)' }} />
          <div className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(circle at 50% 45%, transparent 40%, rgba(0,0,0,0.65) 100%)' }} />

          <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 }}
              className="font-mono text-[#39FF14]" style={{ textShadow: '0 0 18px rgba(57,255,20,0.55)' }}>
              <div className="text-xs uppercase tracking-[0.4em]">// classified access granted</div>
              <div className="mt-4 text-4xl sm:text-6xl font-bold tracking-tight">MATRIX MODE</div>
              <div className="mt-3 text-sm text-[#7CFF6B]">{t('matrix.subtitle')}</div>
            </motion.div>

            <div className="mt-10 flex items-center gap-3">
              <button onClick={disable} data-testid="wow-matrix-exit"
                className="inline-flex items-center gap-2 rounded-full border border-[#39FF14]/50 bg-[#39FF14]/10 px-5 py-2.5 font-mono text-sm text-[#39FF14] hover:bg-[#39FF14]/20 transition-[background-color] duration-200">
                <X className="h-4 w-4" /> {t('matrix.exit')} (ESC)
              </button>
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 font-mono text-[11px] text-[#39FF14]/70">
              <TerminalSquare className="h-3.5 w-3.5" /> IT-WELT360 · secure-core · you found the easter egg
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
